import { DisciplineLiveStatus, EventStatus, ModelTypes } from '@om-fe/models';
import { createBem, BemMethod, isLiveStatus, PlatformTypes } from '@om-fe/utils';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import find from 'lodash/find';
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { AppState } from '@om-fe/core/core/types';
import getSlice from '@om-fe/core/utils/get-slice';
import EventAnchor from '@om-fe/core/components/event-anchor/event-anchor';
import { FavoriteButton } from '@om-fe/core/components/favorites/favorite.utils';
import getConfig, { statScoreV2Item } from '@om-fe/core/utils/config';
import {
    FilteredEventStatistics,
    FilteredMatchesWithEvents,
    MatchListStoreModel,
} from '../match-list/utils/match-list-models';
import { EventItemClassName } from './event-item.utils';
import MatchInfoV2 from './match-info-v2';
import OddsGroup from './odds-group';
import { BrowserOnly } from '@om-fe/widgets/dist/components/ssr/browser-only';
import MatchDetailsSidebarBetradar from '../match-details/match-details-sidebar-betradar';
import BetRadarStatistics from '@om-fe/core/components/betradar-statistics/betradar-statistics';
import { MatchListItemLayout } from '../match-list/utils/match-list.constants';
import values from 'lodash/values';
import { BonusType } from '../match-list-group/match-list-special-header';
import { StatScoreWidgetV2 } from '@om-fe/widgets/components/stat-score/statscore-widget';
import MarketGroups from '@om-fe/core/components/market-groups/market-groups';
import { setActiveMarketGroup } from '../match-list/containers/tournament-match-list/tournament-match-list.actions';
import { ItemWidthType } from '@om-fe/widgets/dist/components/nav/nav.constants';

export interface EventItemPublicProps {
    readonly extraClassName?: string | string[];
    readonly isActive: boolean;
    readonly isExpanded?: boolean;
    readonly isExpandable?: boolean;
    readonly matchId: string;
    readonly sliceKey: string;
    readonly liveStatus?: DisciplineLiveStatus;
    readonly routeName?: RouteNames;
    readonly mainMarketsLimit?: number;
    readonly isStatisticExpanded?: boolean;
    readonly instanceId: string;
    readonly setActiveStatistic?: (instanceId: string) => void,
    readonly modelType: ModelTypes;
    readonly specialMatchList?: boolean;
    readonly itemLayout?: MatchListItemLayout;
    readonly bonusType?: BonusType;
    readonly isVirtual?: boolean;
    readonly matchListInstance?: string;
}

export interface EventItemStoreProps {
    readonly filteredMainMarketsList?: string[];
    readonly match?: FilteredMatchesWithEvents;
    readonly numberOfMarkets?: number;
    readonly isLive?: boolean;
    readonly providerEntityId?: string;
    readonly hasStreamingProviderId?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly activeMarketGroup?: Record<string, string>;
    readonly statistics?: FilteredEventStatistics;
}

interface EventItemDispatchProps {
    readonly setMarketGroup?: (instanceId: string, matchId: string) => void;
}

interface EventItemState {
    readonly isExpanded?: boolean;
}

const bem = createBem({
    name: EventItemClassName,
});

// TODO: ADD MarketName from market

const nameGroup = bem('MatchDetails');
const nameGroupOdds = bem('OddsGroup');
const nameGroupHeaderOdds = bem('OddsGroupHeader');

export type EventItemProps = EventItemPublicProps
    & EventItemStoreProps
    & I18nContextInterface
    & EventItemDispatchProps;

class EventItemComponent extends React.PureComponent<EventItemProps, EventItemState> {
    constructor(props: EventItemProps) {
        super(props);
        const { isExpanded, isExpandable } = props;

        this.state = {};

        if (isExpandable) {
            this.state = {
                ...this.state,
                isExpanded,
            };
        }
    }

    getOddsGroupNode = (): React.ReactNode => {
        const { filteredMainMarketsList, matchId, sliceKey, mainMarketsLimit, modelType, itemLayout, gettext, bonusType } = this.props;

        return (
            <div className={nameGroupOdds}>
                {itemLayout === MatchListItemLayout.Special && (
                    <span className={nameGroupHeaderOdds}>
                        {bonusType === BonusType.Single ? (
                            gettext('Bonus Dana')
                        ) : (
                            gettext('Bonus Plus')
                        )}
                    </span>
                )}
                {filteredMainMarketsList && filteredMainMarketsList.map((mainMarketId: string, index: number) => {
                    if (index >= mainMarketsLimit) {
                        return null;
                    }
                    const showOddsBar: boolean = index === 0 || false; 

                    return (
                        <OddsGroup
                            key={mainMarketId}
                            mainMarketId={mainMarketId}
                            matchId={matchId}
                            sliceKey={sliceKey}
                            inheritedBem={bem}
                            showOddsBar={showOddsBar}
                            modelType={modelType}
                        />
                    );
                })}
            </div>
        );
    };

    
    handleStatisticButton = (instanceId: string): void => {
        const { setActiveStatistic } = this.props;

        setActiveStatistic(instanceId);
    };

    getMatchDetailsNode = (): React.ReactNode => {
        const {
            match,
            matchId,
            sliceKey,
            isLive,
            routeName,
            providerEntityId,
            hasStreamingProviderId,
            isStatisticExpanded,
            modelType,
            isVirtual,
            matchListInstance,
            statistics,
            itemLayout,
        } = this.props;

        const { isAvailableForSimulation } = match ?? {};
        const statScoreConfig: statScoreV2Item | undefined = getConfig().statScoreWidgetV2Config?.group;
        const hasSimulateBetButton: boolean = !!getConfig().simulate?.showSimulateBetButton;
        const isOutright: boolean = modelType === ModelTypes.TOURNAMENT ? true : false;
        const bemStatistics: BemMethod = createBem({
            name: statScoreConfig ? 'StatScoreStatistics' : 'BetradarStatistics',
        });
        const showEventFavorite = isLive && !isOutright && !isVirtual;

        return (
            <div className={nameGroup}>
                <div className={bem('FirstColumnIndicators')}>
                    {showEventFavorite && (
                        <FavoriteButton matchId={matchId} />
                    )}
                    {providerEntityId && isVirtual &&
                        <div className={bem('StatisticsButton')}>
                            <BetRadarStatistics betRadarEventId={providerEntityId} isVirtual={true} statistics={statistics} />
                        </div>
                    }
                    {!hasStreamingProviderId && providerEntityId && !isVirtual && (
                        <div className={bem('StatisticsButton')}>
                            <div
                                className={bemStatistics({
                                    modifiers: {
                                        Active: isStatisticExpanded,
                                    },
                                })}
                                onClick={() => this.handleStatisticButton(`statistic_${matchId}`)}
                            >
                                <GeneralIcon name={'stats'} />
                            </div>
                        </div>
                    )}
                    {hasSimulateBetButton && !!isAvailableForSimulation && (
                        <div className={bem('BettingSimulationIcon')}>
                            <GeneralIcon name='goggle' />
                        </div>
                    )}
                </div>
                <MatchInfoV2
                    matchId={matchId}
                    sliceKey={sliceKey}
                    routeName={routeName}
                    bemItem={bem}
                    hasStreamingProviderId={hasStreamingProviderId}
                    modelType={modelType}
                    itemLayout={itemLayout}
                    isVirtual={isVirtual}
                    matchListInstance={matchListInstance}
                />
            </div>
        );
    };

    getNumberOfMarketsNode = (): React.ReactNode => {
        const {
            isVirtual,
            match,
            matchId,
            isLive,
            numberOfMarkets,
            matchListInstance,
            setMarketGroup,
        } = this.props;

        if (!match) {
            return null;
        }

        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const locationName = isHyphenatedFullUrl ? match?.venueName : match?.shortVenueName;
        const disciplineName = isHyphenatedFullUrl ? match?.sportName : match?.shortSportName;
        const tournamentName = isHyphenatedFullUrl ? match?.parentName : match?.shortParentName;

        const {
            sportId: disciplineId, name: eventName,
        } = match;

        return (
            isVirtual && matchListInstance ? (
                <div
                    className={bem('Indicator')}
                    onClick={() => setMarketGroup?.(matchListInstance, matchId)}
                >
                    {numberOfMarkets && (
                        <>
                            <span className={bem('IndicatorPart')}>
                                {numberOfMarkets}
                            </span>
                            <GeneralIcon name="arr-right" />
                        </>
                    )}
                </div>
            ) : (
                <EventAnchor
                    className={bem('Indicator')}
                    isLive={!!isLive}
                    disciplineId={disciplineId}
                    disciplineName={disciplineName}
                    locationName={locationName}
                    tournamentName={tournamentName}
                    eventId={matchId}
                    eventName={eventName}
                >
                    {numberOfMarkets && (
                        <>
                            <span className={bem('IndicatorPart')}>
                                {numberOfMarkets}
                            </span>
                            <GeneralIcon name="arr-right" />
                        </>
                    )}
                </EventAnchor>
            )
        );
    };

    toggleExpanded = (): void => {
        const { isExpanded } = this.state;

        if (isExpanded === undefined) {
            return;
        }
        this.setState({
            isExpanded: !isExpanded,
        });
    };

    render() {
        const {
            extraClassName,
            filteredMainMarketsList,
            isActive,
            isExpandable,
            matchId,
            providerEntityId,
            isStatisticExpanded,
            liveStatus,
            selectedLanguage,
            match,
            isLive,
            deviceType,
            matchListInstance,
            isVirtual,
            activeMarketGroup,
        } = this.props;
        const statScoreConfig: statScoreV2Item | undefined = getConfig().statScoreWidgetV2Config?.group;
        const { isExpanded } = this.state;
        const { sportId } = match
        const isDesktop: boolean = deviceType === 'desktop';
        const isTablet: boolean = deviceType === 'tablet';
        const isMobile: boolean = deviceType === 'mobile';
        const {
            desktop: hideOnDesktop = false,
            tablet: hideOnTablet = false,
            mobile: hideOnMobile = false,
        } = getConfig()?.numberOfMarketsUnderTeamsName ?? {};
        const hiddenNumberOfMarkets: boolean = (hideOnDesktop && isDesktop) || (hideOnTablet && isTablet) || (hideOnMobile && isMobile);

        if (!filteredMainMarketsList) {
            return null;
        }

        return (
            <>
                <div className={bem({
                    modifiers: {
                        active: isActive,
                        isExpanded: !!isExpanded,
                        isLive: isLive,
                    },
                    extra: extraClassName,
                })}>
                    {this.getMatchDetailsNode()}
                    {isExpandable && (
                        <button
                            className={bem('OddsCollapseBtn')}
                            onClick={this.toggleExpanded}
                        >
                            <GeneralIcon name={isExpanded ? 'arr-up' : 'arr-down'} />
                        </button>
                    )}
                    {isExpandable ?
                        isExpanded && this.getOddsGroupNode()
                        : this.getOddsGroupNode()
                    }
                    {!hiddenNumberOfMarkets && this.getNumberOfMarketsNode()}
                </div>
                {isVirtual && activeMarketGroup && activeMarketGroup[matchId] &&
                    <MarketGroups
                        matchId={matchId}
                        instanceId={`${matchListInstance}-${matchId}`}
                        disableRoute={true}
                        showMarketGroupsHeader={true}
                        itemWidthType={ItemWidthType.ItemToStartUnequalWidth}
                        activeMarketGroupId={'all'}
                    />
                }
                {statScoreConfig && !!providerEntityId && isStatisticExpanded &&
                    <BrowserOnly>
                        <StatScoreWidgetV2
                            statscoreWidgetId={providerEntityId}
                            configurationId={isLive ? statScoreConfig.live : statScoreConfig.pending}
                            lang={selectedLanguage}
                            statScoreWidgetV2Group={true}
                        />
                    </BrowserOnly>
                }
                {!statScoreConfig && !!providerEntityId && isStatisticExpanded &&
                    <>
                        <BrowserOnly>
                            <MatchDetailsSidebarBetradar
                                disciplineId={sportId}
                                matchId={matchId}
                                lang={selectedLanguage}
                                liveStatus={liveStatus}
                            />
                        </BrowserOnly>
                    </>
                }
            </>
        );
    }
}

const EventItem: React.ComponentType<EventItemPublicProps> = compose(
    withTranslation<EventItemPublicProps>(),
    connect<EventItemStoreProps>(
        (state: AppState, props: EventItemPublicProps): EventItemStoreProps => {
            const { sliceKey, matchId, itemLayout } = props;
            const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, sliceKey);

            if (!slice || !matchId) {
                return {};
            }

            const { filteredMainMarketsList, filteredMainMarkets, activeMarketGroup } = slice;
            const match: FilteredMatchesWithEvents | undefined = slice.filteredMatchesWithEvents[matchId];
            const isLive: boolean = !!match && isLiveStatus(match.statusId as EventStatus);
            const statistics: FilteredEventStatistics | undefined = slice.filteredEventStatistics.find(
                statistic => statistic.entityId === matchId);
            const providerEntityId: string | undefined = statistics?.providerEntityId;

            let filterMarket: string[] | undefined = filteredMainMarketsList;

            if (itemLayout === MatchListItemLayout.Special) {
                const findMarket = find(values(filteredMainMarkets), { eventId: matchId });
                filterMarket = findMarket ? [findMarket?.id] : [];
            }

            return {
                match,
                isLive,
                filteredMainMarketsList: filterMarket,
                numberOfMarkets: match.numberOfMarkets,
                providerEntityId,
                hasStreamingProviderId: !!find(slice.filteredStreamingProviderEventRelation, { eventId: matchId }),
                deviceType: state.deviceInfo?.platform.type,
                activeMarketGroup,
                statistics,
            };
        },
        {
            setMarketGroup: setActiveMarketGroup,
        }
    ))(EventItemComponent);

export default EventItem;
